import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookmarkPlus, Check, CheckCircle2, Download, Loader2, MapPin, Plus, Search, Star, Trash2, X, XCircle, Zap } from "lucide-react";
import api, { inr, fmtErr } from "../../lib/api";
import StatusBadge from "../../components/StatusBadge";
import { toast } from "sonner";

const STEPS = ["Pickup", "Receiver", "Order", "Package", "Other", "Operator", "Done"];
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-shipany-navy placeholder:text-slate-400 focus:border-shipany-blue focus:outline-none focus:ring-2 focus:ring-shipany-blue/15";

const NAME_RE = /^[A-Za-z][A-Za-z .'-]*$/;
const ADDRESS_RE = /^[A-Za-z0-9\s,./#&()'-]+$/;
const ADDR_HINT = "Letters, numbers and , . - / # & ( ) ' only — no special characters";
const MAX_BOX_DIMENSION_CM = 306;
const EWAY_RE = /^[A-Za-z0-9]{12}$/;
const EMPTY_ITEM = { name: "", hsn: "", qty: "1", price: "", weight: "" };

const CONFETTI_COLORS = ["#0B5CD7", "#12C2E9", "#FBBF24", "#10B981", "#F472B6", "#8B5CF6"];

// Celebration burst on booking success — pure CSS, no library.
function ConfettiBurst() {
  const pieces = useMemo(() => Array.from({ length: 56 }, (_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 0.5,
    duration: 2.4 + Math.random() * 1.6,
    size: 6 + Math.random() * 6,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    drift: -60 + Math.random() * 120,
    round: Math.random() > 0.5,
  })), []);
  return (
    <div className="pointer-events-none fixed inset-0 z-[60] overflow-hidden" data-testid="confetti-burst" aria-hidden="true">
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(-8vh) translateX(0) rotate(0deg); opacity: 1; }
          85% { opacity: 1; }
          100% { transform: translateY(105vh) translateX(var(--drift)) rotate(720deg); opacity: 0; }
        }
        @keyframes success-pop {
          0% { transform: scale(0.5); opacity: 0; }
          60% { transform: scale(1.12); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
      {pieces.map((p, i) => (
        <span key={i} className="absolute -top-4 block"
          style={{
            left: `${p.left}%`, width: p.size, height: p.round ? p.size : p.size * 0.55,
            background: p.color, borderRadius: p.round ? "50%" : 2,
            animation: `confetti-fall ${p.duration}s linear ${p.delay}s forwards`,
            "--drift": `${p.drift}px`,
          }} />
      ))}
    </div>
  );
}

export default function CreateShipment() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [warehouses, setWarehouses] = useState([]);
  const [form, setForm] = useState({
    warehouse_id: "",
    delivery_name: "", delivery_phone: "", delivery_alt_phone: "", delivery_email: "", receiver_gstin: "",
    delivery_address: "", delivery_address2: "", delivery_city: "", delivery_state: "", delivery_pincode: "",
    order_date: new Date().toISOString().slice(0, 10), reference_id: "", invoice_number: "",
    items: [{ ...EMPTY_ITEM }],
    payment_mode: "Prepaid", cod_amount: "", declared_value: "", is_insured: false,
    package_type: "box", weight: "0.5", length: "10", breadth: "10", height: "10",
    shipment_class: "box", boxes: "1", box_list: [],
    reseller_name: "", seller_gstin: "", eway_bill: "",
  });
  const [rates, setRates] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(null);
  const [pinStatus, setPinStatus] = useState(null);
  const [recvQuery, setRecvQuery] = useState("");
  const [recvResults, setRecvResults] = useState([]);
  const [recvSearching, setRecvSearching] = useState(false);
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");
  const [presetSaving, setPresetSaving] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [rateSort, setRateSort] = useState("price");
  const [rateQuery, setRateQuery] = useState("");

  useEffect(() => {
    api.get("/warehouses").then(({ data }) => {
      setWarehouses(data);
      const def = data.find((w) => w.is_default) || data[0];
      if (def) setForm((f) => ({ ...f, warehouse_id: def.warehouse_id }));
    }).catch(() => {});
    api.get("/package-presets").then(({ data }) => setPresets(data)).catch(() => {});
  }, []);

  // Load a saved draft (?draft=<id>) — resume and assign a courier later.
  useEffect(() => {
    const id = searchParams.get("draft");
    if (!id) return;
    api.get(`/drafts/${id}`).then(({ data }) => {
      if (data?.payload?.form) {
        setForm((f) => ({ ...f, ...data.payload.form }));
        setStep(Math.min(data.payload.step || 0, 5));
        setDraftId(id);
        toast.info("Draft loaded — continue where you left off");
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const warehouse = useMemo(() => warehouses.find((w) => w.warehouse_id === form.warehouse_id), [warehouses, form.warehouse_id]);

  // Repeat-customer search (Shipmozo-style: search past receivers by phone or name)
  useEffect(() => {
    const q = recvQuery.trim();
    if (q.length < 3) { setRecvResults([]); return; }
    setRecvSearching(true);
    const t = setTimeout(() => {
      api.get("/receivers/search", { params: { q } })
        .then(({ data }) => setRecvResults(data))
        .catch(() => {})
        .finally(() => setRecvSearching(false));
    }, 350);
    return () => clearTimeout(t);
  }, [recvQuery]);

  const applyReceiver = (r) => {
    setForm((f) => ({
      ...f,
      delivery_name: r.name || f.delivery_name, delivery_phone: r.phone || f.delivery_phone,
      delivery_alt_phone: r.alt_phone || "", delivery_email: r.email || "", receiver_gstin: r.gstin || "",
      delivery_address: r.address || "", delivery_address2: r.address2 || "",
      delivery_pincode: r.pincode || "", delivery_city: r.city || "", delivery_state: r.state || "",
    }));
    setRecvResults([]);
    setRecvQuery("");
    toast.success(`Receiver details filled for ${r.name}`);
  };

  // Pincode serviceability + city/state autofill
  useEffect(() => {
    if (!/^\d{6}$/.test(form.delivery_pincode) || !warehouse?.pincode) { setPinStatus(null); return; }
    let alive = true;
    setPinStatus({ checking: true });
    (async () => {
      try {
        const { data } = await api.get(`/serviceability/pincode?pickup=${warehouse.pincode}&delivery=${form.delivery_pincode}`);
        if (alive) setPinStatus({ serviceable: !!data.serviceable, mode: data.mode, cod_available: !!data.cod_available });
      } catch { if (alive) setPinStatus({ serviceable: false }); }
      api.get(`/pincode/${form.delivery_pincode}`).then(({ data: loc }) => {
        if (alive && loc) setForm((f) => ({ ...f, delivery_city: loc.city || f.delivery_city, delivery_state: loc.state || f.delivery_state }));
      }).catch(() => {});
    })();
    return () => { alive = false; };
  }, [form.delivery_pincode, warehouse?.pincode]);

  const setItem = (i, key, value) => setForm((f) => ({ ...f, items: f.items.map((it, j) => (j === i ? { ...it, [key]: value } : it)) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, j) => j !== i) }));

  const fetchRates = async () => {
    setLoadingRates(true);
    setRates(null);
    setSelected(null);
    setRateQuery("");
    try {
      const { data } = await api.post("/rates/calculate", {
        pickup_pincode: warehouse.pincode,
        delivery_pincode: form.delivery_pincode,
        weight: parseFloat(form.weight) || 0.5,
        length: parseFloat(form.length) || 10,
        breadth: parseFloat(form.breadth) || 10,
        height: parseFloat(form.height) || 10,
        boxes: parseInt(form.boxes) || 1,
        ...(form.shipment_class === "heavy" ? { box_details: (form.box_list || []).map((b) => ({ weight: parseFloat(b.weight) || 0, length: parseFloat(b.length) || 10, breadth: parseFloat(b.breadth) || 10, height: parseFloat(b.height) || 10 })) } : {}),
        payment_mode: form.payment_mode,
        cod_amount: parseFloat(form.cod_amount) || 0,
        package_type: form.package_type,
      });
      setRates(data);
      const rec = data.quotes.find((q) => q.recommended) || data.quotes[0];
      if (rec) setSelected(rec.courier_id);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLoadingRates(false);
    }
  };

  const visibleQuotes = useMemo(() => {
    if (!rates?.quotes) return [];
    let qs = [...rates.quotes];
    if (form.payment_mode === "COD") qs = qs.filter((q) => q.cod_supported !== false);
    const q = rateQuery.trim().toLowerCase();
    if (q) qs = qs.filter((x) => x.courier_name.toLowerCase().includes(q));
    qs.sort((a, b) => (rateSort === "eta" ? (a.eta_days || 99) - (b.eta_days || 99) || a.final_price - b.final_price : a.final_price - b.final_price));
    return qs;
  }, [rates, rateSort, rateQuery, form.payment_mode]);

  const sel = rates?.quotes?.find((q) => q.courier_id === selected);

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      const { data } = await api.post("/drafts", { draft_id: draftId, payload: { form, step } });
      setDraftId(data.draft_id);
      toast.success("Draft saved — resume it anytime from Shipments");
    } catch (e) { toast.error(fmtErr(e)); } finally { setSavingDraft(false); }
  };

  const applyPreset = (id) => {
    const p = presets.find((x) => x.preset_id === id);
    if (!p) return;
    setForm((f) => ({ ...f, weight: String(p.weight), length: String(p.length), breadth: String(p.breadth), height: String(p.height) }));
    toast.success(`Preset "${p.name}" applied`);
  };

  const savePreset = async () => {
    if (!presetName.trim()) return toast.error("Give the preset a name");
    setPresetSaving(true);
    try {
      const { data } = await api.post("/package-presets", {
        name: presetName.trim(), weight: parseFloat(form.weight) || 0,
        length: parseFloat(form.length) || 10, breadth: parseFloat(form.breadth) || 10, height: parseFloat(form.height) || 10,
      });
      setPresets((p) => [data, ...p]);
      setPresetName("");
      toast.success(`Preset "${data.name}" saved`);
    } catch (e) { toast.error(fmtErr(e)); } finally { setPresetSaving(false); }
  };

  const deletePreset = async (id) => {
    try {
      await api.delete(`/package-presets/${id}`);
      setPresets((p) => p.filter((x) => x.preset_id !== id));
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const next = () => {
    if (step === 0 && !form.warehouse_id) return toast.error("Select a pickup warehouse");
    if (step === 1) {
      if (!NAME_RE.test(form.delivery_name.trim())) return toast.error("Enter the receiver's name (letters only)");
      if (!/^[6-9]\d{9}$/.test(form.delivery_phone)) return toast.error("Enter a valid 10-digit mobile number");
      if (!/^\d{6}$/.test(form.delivery_pincode)) return toast.error("Enter a 6-digit delivery pincode");
      if (pinStatus?.checking) return toast.error("Verifying pincode serviceability…");
      if (pinStatus && !pinStatus.serviceable) return toast.error("Delivery pincode is not serviceable on this lane — please check it");
      if (form.delivery_address.trim().length < 3 || !ADDRESS_RE.test(form.delivery_address.trim())) return toast.error(`Address line 1: ${ADDR_HINT.toLowerCase()}`);
      if (form.delivery_address2.trim() && !ADDRESS_RE.test(form.delivery_address2.trim())) return toast.error(`Address line 2: ${ADDR_HINT.toLowerCase()}`);
      if (form.delivery_alt_phone && !/^[6-9]\d{9}$/.test(form.delivery_alt_phone)) return toast.error("Alternative phone must be a valid 10-digit mobile");
    }
    if (step === 2) {
      for (const it of form.items) {
        if (!it.name.trim()) return toast.error("Every item needs a name");
        if (!(parseInt(it.qty) >= 1)) return toast.error(`Quantity for "${it.name || "item"}" must be at least 1`);
        if (!(parseFloat(it.price) > 0)) return toast.error(`Unit price for "${it.name || "item"}" must be greater than zero`);
        if (!(parseFloat(it.weight) > 0)) return toast.error(`Weight for "${it.name || "item"}" is required`);
      }
      if (form.payment_mode === "COD" && !(parseFloat(form.cod_amount) > 0)) return toast.error("Enter the COD amount to collect");
    }
    if (step === 3) {
      if (form.shipment_class === "heavy") {
        const list = form.box_list || [];
        if (!list.length) return toast.error("Enter the number of boxes");
        for (const b of list) {
          if (!(parseFloat(b.weight) > 0)) return toast.error("Every box needs a weight");
          for (const k of ["length", "breadth", "height"]) {
            const v = parseFloat(b[k]) || 0;
            if (v <= 0) return toast.error("Every box needs all dimensions");
            if (v > MAX_BOX_DIMENSION_CM) return toast.error(`Box dimensions cannot exceed ${MAX_BOX_DIMENSION_CM} cm per side`);
          }
        }
      } else {
        const w = parseFloat(form.weight) || 0;
        if (form.package_type === "document" && w > 2) return toast.error("Documents are supported up to 2 kg");
        if (form.package_type === "box" && w < 0.5) return toast.error("Minimum weight for a box parcel is 0.5 kg");
        for (const k of ["length", "breadth", "height"]) {
          const v = parseFloat(form[k]) || 0;
          if (v <= 0) return toast.error("Enter all package dimensions");
          if (v > MAX_BOX_DIMENSION_CM) return toast.error(`Dimensions cannot exceed ${MAX_BOX_DIMENSION_CM} cm per side`);
        }
      }
    }
    if (step === 4) {
      if (form.eway_bill && !EWAY_RE.test(form.eway_bill.trim())) return toast.error("E-way bill number must be exactly 12 characters");
      fetchRates();
    }
    setStep(Math.min(step + 1, 6));
  };

  const book = async () => {
    if (!sel) return;
    setBooking(true);
    try {
      const items = form.items.map((it) => ({
        name: it.name.trim(), hsn: it.hsn.trim(), qty: parseInt(it.qty) || 1,
        price: parseFloat(it.price) || 0, weight: parseFloat(it.weight) || 0, sku: "",
      }));
      const { data } = await api.post("/shipments/book", {
        warehouse_id: form.warehouse_id, courier_id: selected,
        delivery_name: form.delivery_name.trim(), delivery_phone: form.delivery_phone,
        delivery_alt_phone: form.delivery_alt_phone, delivery_email: form.delivery_email, receiver_gstin: form.receiver_gstin,
        delivery_address: form.delivery_address.trim(), delivery_address2: form.delivery_address2.trim(),
        delivery_city: form.delivery_city, delivery_state: form.delivery_state, delivery_pincode: form.delivery_pincode,
        package_type: form.package_type,
        weight: parseFloat(form.weight) || 0.5,
        length: parseFloat(form.length) || 10, breadth: parseFloat(form.breadth) || 10, height: parseFloat(form.height) || 10,
        boxes: parseInt(form.boxes) || 1,
        ...(form.shipment_class === "heavy" ? { box_details: (form.box_list || []).map((b) => ({ weight: parseFloat(b.weight) || 0, length: parseFloat(b.length) || 10, breadth: parseFloat(b.breadth) || 10, height: parseFloat(b.height) || 10 })) } : {}),
        payment_mode: form.payment_mode, cod_amount: parseFloat(form.cod_amount) || 0,
        declared_value: parseFloat(form.declared_value) || 0,
        items,
        customer_reference: form.reference_id.trim(), invoice_number: form.invoice_number.trim(),
        eway_bill: form.eway_bill.trim(), reseller_name: form.reseller_name.trim(), is_insured: form.is_insured,
      }, { loaderLabel: "Booking your shipment…", loaderCourier: sel?.courier_name });
      setBooked(data);
      setStep(6);
      if (draftId) { api.delete(`/drafts/${draftId}`).catch(() => {}); setDraftId(null); }
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBooking(false);
    }
  };

  const itemsTotal = form.items.reduce((s, it) => s + (parseFloat(it.price) || 0) * (parseInt(it.qty) || 0), 0);

  return (
    <div className="mx-auto max-w-4xl" data-testid="create-shipment-page">
      <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Create Shipment</h1>
      <div className="mt-6 flex items-center gap-0" data-testid="wizard-steps">
        {STEPS.map((s, i) => (
          <div key={s} className="flex flex-1 items-center">
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${i < step ? "bg-emerald-500 text-white" : i === step ? "bg-shipany-blue text-white glow-blue" : "bg-slate-200 text-slate-500"}`}>
              {i < step ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            <span className={`ml-2 hidden text-xs font-semibold sm:block ${i === step ? "text-shipany-navy" : "text-slate-400"}`}>{s}</span>
            {i < STEPS.length - 1 && <span className={`mx-2 h-0.5 flex-1 ${i < step ? "bg-emerald-400" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        {step === 0 && (
          <div data-testid="wizard-step-pickup">
            <h2 className="font-display text-lg font-bold text-shipany-navy">Pickup warehouse</h2>
            {warehouses.length === 0 ? (
              <div className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
                No warehouses yet. <Link to="/app/warehouses" className="font-bold underline">Create one first</Link>.
              </div>
            ) : (
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {warehouses.map((w) => (
                  <button key={w.warehouse_id} data-testid={`warehouse-option-${w.warehouse_id}`} onClick={() => setForm({ ...form, warehouse_id: w.warehouse_id })}
                    className={`rounded-xl border-2 p-4 text-left transition-all ${form.warehouse_id === w.warehouse_id ? "border-shipany-blue bg-shipany-blue/5" : "border-slate-200 hover:border-slate-300"}`}>
                    <p className="font-semibold text-shipany-navy">{w.name} {w.is_default && <span className="ml-1 rounded bg-shipany-cyan/15 px-1.5 py-0.5 text-[10px] font-bold text-shipany-blue">DEFAULT</span>}</p>
                    <p className="mt-1 text-xs text-slate-500">{w.address}, {w.city} — <span className="mono">{w.pincode}</span></p>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 1 && (
          <div data-testid="wizard-step-customer">
            <h2 className="font-display text-lg font-bold text-shipany-navy">Buyer / Receiver details</h2>
            <div className="relative mt-4">
              <label className="text-xs font-semibold text-slate-500">Repeat customer? Search by phone or name</label>
              <div className="relative mt-1">
                <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input data-testid="receiver-search-input" className={`${inputCls} pl-10`} placeholder="Type at least 3 characters…" value={recvQuery} onChange={(e) => setRecvQuery(e.target.value)} />
                {recvSearching && <Loader2 className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400" />}
              </div>
              {recvResults.length > 0 && (
                <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg" data-testid="receiver-search-results">
                  {recvResults.map((r, i) => (
                    <button key={r.phone || i} type="button" data-testid={`receiver-option-${i}`} onClick={() => applyReceiver(r)}
                      className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm hover:bg-shipany-blue/5">
                      <span className="font-semibold text-shipany-navy">{r.name}</span>
                      <span className="mono text-xs text-slate-500">{r.phone} · {r.pincode}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div><label className="text-xs font-semibold text-slate-500">Receiver mobile *</label>
                <input data-testid="delivery-phone-input" className={`${inputCls} mono mt-1`} placeholder="10 digits" inputMode="numeric" maxLength={10} value={form.delivery_phone} onChange={(e) => setForm({ ...form, delivery_phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} /></div>
              <div><label className="text-xs font-semibold text-slate-500">Receiver name *</label>
                <input data-testid="delivery-name-input" className={`${inputCls} mt-1`} placeholder="Letters only" value={form.delivery_name} onChange={(e) => setForm({ ...form, delivery_name: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-slate-500">Alternative phone</label>
                <input data-testid="delivery-alt-phone-input" className={`${inputCls} mono mt-1`} placeholder="Optional" inputMode="numeric" maxLength={10} value={form.delivery_alt_phone} onChange={(e) => setForm({ ...form, delivery_alt_phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} /></div>
              <div><label className="text-xs font-semibold text-slate-500">Email</label>
                <input data-testid="delivery-email-input" className={`${inputCls} mt-1`} placeholder="e.g. buyer@example.com" value={form.delivery_email} onChange={(e) => setForm({ ...form, delivery_email: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-slate-500">Receiver GSTIN</label>
                <input data-testid="receiver-gstin-input" className={`${inputCls} mono mt-1`} placeholder="Optional — for B2B buyers" maxLength={15} value={form.receiver_gstin} onChange={(e) => setForm({ ...form, receiver_gstin: e.target.value.toUpperCase() })} /></div>
              <div>
                <label className="text-xs font-semibold text-slate-500">Pincode *</label>
                <input data-testid="delivery-pincode-input" className={`${inputCls} mono mt-1`} placeholder="6 digits" inputMode="numeric" maxLength={6} value={form.delivery_pincode} onChange={(e) => setForm({ ...form, delivery_pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })} />
                {/^\d{6}$/.test(form.delivery_pincode) && (
                  <p className="mt-1.5 flex items-center gap-1 text-[11px] font-semibold" data-testid="pincode-verify-status">
                    {pinStatus?.checking ? <span className="flex items-center gap-1 text-slate-400"><Loader2 className="h-3 w-3 animate-spin" /> Verifying serviceability…</span>
                      : pinStatus?.serviceable ? <span className="flex items-center gap-1 text-emerald-600"><CheckCircle2 className="h-3 w-3" /> Serviceable{pinStatus.mode === "live" ? " · live check" : ""}{pinStatus.cod_available ? " · COD available" : ""}</span>
                      : pinStatus ? <span className="flex items-center gap-1 text-red-500"><XCircle className="h-3 w-3" /> Not serviceable on this lane</span> : null}
                  </p>
                )}
              </div>
              <div className="sm:col-span-2"><label className="text-xs font-semibold text-slate-500">Address line 1 *</label>
                <input data-testid="delivery-address-input" className={`${inputCls} mt-1`} placeholder="Flat, house no., building, apartment" maxLength={120} value={form.delivery_address} onChange={(e) => setForm({ ...form, delivery_address: e.target.value })} /></div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-slate-500">Address line 2</label>
                <input data-testid="delivery-address2-input" className={`${inputCls} mt-1`} placeholder="Area, colony, street, landmark (optional)" maxLength={120} value={form.delivery_address2} onChange={(e) => setForm({ ...form, delivery_address2: e.target.value })} />
                <p className="mt-1 text-[10px] text-slate-400">{ADDR_HINT}.</p>
              </div>
              <div><label className="text-xs font-semibold text-slate-500">City (auto-filled from pincode)</label>
                <input data-testid="delivery-city-input" className={`${inputCls} mt-1 read-only:bg-slate-50 read-only:text-slate-500`} placeholder="Auto-filled" value={form.delivery_city} readOnly title="Auto-filled from pincode" /></div>
              <div><label className="text-xs font-semibold text-slate-500">State (auto-filled from pincode)</label>
                <input data-testid="delivery-state-input" className={`${inputCls} mt-1 read-only:bg-slate-50 read-only:text-slate-500`} placeholder="Auto-filled" value={form.delivery_state} readOnly title="Auto-filled from pincode" /></div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="wizard-step-order">
            <h2 className="font-display text-lg font-bold text-shipany-navy">Order details</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <div><label className="text-xs font-semibold text-slate-500">Order date</label>
                <input type="date" data-testid="order-date-input" className={`${inputCls} mono mt-1`} value={form.order_date} onChange={(e) => setForm({ ...form, order_date: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-slate-500">Reference ID</label>
                <input data-testid="reference-id-input" className={`${inputCls} mono mt-1`} placeholder="Your order/ref id" maxLength={40} value={form.reference_id} onChange={(e) => setForm({ ...form, reference_id: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-slate-500">Invoice number</label>
                <input data-testid="invoice-number-input" className={`${inputCls} mono mt-1`} placeholder="Optional" maxLength={30} value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} /></div>
            </div>

            <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-slate-400">Products in this order</p>
            <div className="mt-2 space-y-3" data-testid="items-list">
              {form.items.map((it, i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-slate-50/50 p-4" data-testid={`item-card-${i}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Item {i + 1}</p>
                    {form.items.length > 1 && (
                      <button type="button" onClick={() => removeItem(i)} data-testid={`remove-item-${i}-btn`} className="flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /> Remove</button>
                    )}
                  </div>
                  <div className="mt-2 grid gap-3 sm:grid-cols-6">
                    <div className="sm:col-span-2"><label className="text-xs font-semibold text-slate-500">Item name *</label>
                      <input data-testid={i === 0 ? "item-name-input" : `item-${i}-name-input`} className={`${inputCls} mt-1`} placeholder="e.g. Cotton T-Shirts" value={it.name} onChange={(e) => setItem(i, "name", e.target.value)} /></div>
                    <div><label className="text-xs font-semibold text-slate-500">HSN</label>
                      <input data-testid={i === 0 ? "item-hsn-input" : `item-${i}-hsn-input`} className={`${inputCls} mono mt-1`} placeholder="Optional" maxLength={8} value={it.hsn} onChange={(e) => setItem(i, "hsn", e.target.value.replace(/\D/g, ""))} /></div>
                    <div><label className="text-xs font-semibold text-slate-500">Qty *</label>
                      <input data-testid={i === 0 ? "item-qty-input" : `item-${i}-qty-input`} className={`${inputCls} mono mt-1`} inputMode="numeric" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value.replace(/\D/g, ""))} /></div>
                    <div><label className="text-xs font-semibold text-slate-500">Unit price (₹) *</label>
                      <input data-testid={i === 0 ? "item-value-input" : `item-${i}-value-input`} className={`${inputCls} mono mt-1`} inputMode="decimal" value={it.price} onChange={(e) => setItem(i, "price", e.target.value.replace(/[^\d.]/g, ""))} /></div>
                    <div><label className="text-xs font-semibold text-slate-500">Weight (kg) *</label>
                      <input data-testid={i === 0 ? "item-weight-input" : `item-${i}-weight-input`} className={`${inputCls} mono mt-1`} inputMode="decimal" placeholder="0.4" value={it.weight} onChange={(e) => setItem(i, "weight", e.target.value.replace(/[^\d.]/g, ""))} /></div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-3">
              <button type="button" onClick={addItem} data-testid="add-item-btn"
                className="flex items-center gap-1.5 rounded-xl border border-dashed border-shipany-blue/40 px-4 py-2 text-xs font-bold text-shipany-blue hover:bg-shipany-blue/5"><Plus className="h-3.5 w-3.5" /> Add another item</button>
              <span className="mono text-xs text-slate-400" data-testid="items-total">Order value: {inr(itemsTotal)}</span>
            </div>

            <div className="mt-6">
              <label className="text-xs font-semibold text-slate-500">Payment mode</label>
              <div className="mt-2 flex gap-3">
                {["Prepaid", "COD"].map((m) => (
                  <button key={m} type="button" data-testid={`payment-mode-${m.toLowerCase()}`} onClick={() => setForm({ ...form, payment_mode: m })}
                    className={`rounded-xl border-2 px-6 py-2.5 text-sm font-semibold transition-all ${form.payment_mode === m ? "border-shipany-blue bg-shipany-blue/5 text-shipany-blue" : "border-slate-200 text-slate-500"}`}>{m}</button>
                ))}
              </div>
            </div>
            {form.payment_mode === "COD" && (
              <div className="mt-4 max-w-xs"><label className="text-xs font-semibold text-slate-500">COD amount (₹) *</label>
                <input data-testid="cod-amount-input" className={`${inputCls} mono mt-1`} inputMode="decimal" value={form.cod_amount} onChange={(e) => setForm({ ...form, cod_amount: e.target.value.replace(/[^\d.]/g, "") })} /></div>
            )}
            <div className="mt-4 flex flex-wrap items-end gap-4">
              <div className="max-w-xs flex-1"><label className="text-xs font-semibold text-slate-500">Shipment value (₹)</label>
                <input data-testid="declared-value-input" className={`${inputCls} mono mt-1`} placeholder="Declared value of goods" inputMode="decimal" value={form.declared_value} onChange={(e) => setForm({ ...form, declared_value: e.target.value.replace(/[^\d.]/g, "") })} />
                <p className="mt-1 text-[11px] text-slate-400">Declared goods value — used for courier liability &amp; invoice.</p></div>
              <label className="flex items-center gap-2 pb-2 text-sm font-semibold text-shipany-navy">
                <input type="checkbox" data-testid="insurance-checkbox" checked={form.is_insured} onChange={(e) => setForm({ ...form, is_insured: e.target.checked })} className="h-4 w-4 rounded border-slate-300 text-shipany-blue focus:ring-shipany-blue/30" />
                Insure this shipment
              </label>
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="wizard-step-package">
            <h2 className="font-display text-lg font-bold text-shipany-navy">Weight &amp; dimensions</h2>

            <div className="mt-6" data-testid="package-type-section">
              <label className="text-xs font-semibold text-slate-500">Package type *</label>
              <div className="mt-2 grid gap-3 sm:grid-cols-3">
                {[
                  { v: "document", t: "Document", d: "Envelopes & papers · up to 2 kg · default 10×10×10 cm (editable)" },
                  { v: "box", t: "Small Shipment", d: "Below 10 kg · single box" },
                  { v: "heavy", t: "Heavy Shipment", d: "10 kg or above · single or multiple boxes" },
                ].map((o) => {
                  const selClass = form.shipment_class || (form.package_type === "document" ? "document" : "box");
                  return (
                    <button key={o.v} type="button" data-testid={`package-type-${o.v}`}
                      onClick={() => setForm((f) => ({
                        ...f,
                        shipment_class: o.v,
                        package_type: o.v === "document" ? "document" : "box",
                        ...(o.v === "document" ? { boxes: "1", length: "10", breadth: "10", height: "10", ...(parseFloat(f.weight) > 2 ? { weight: "2" } : {}) } : {}),
                        ...(o.v === "box" ? { boxes: "1" } : {}),
                      }))}
                      className={`rounded-xl border-2 p-4 text-left transition-all ${selClass === o.v ? "border-shipany-blue bg-shipany-blue/5 glow-blue" : "border-slate-200 hover:border-slate-300"}`}>
                      <p className="font-semibold text-shipany-navy">{o.t}</p>
                      <p className="mt-1 text-xs text-slate-500">{o.d}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {form.shipment_class !== "heavy" && presets.length > 0 && (
              <div className="mt-5 flex flex-wrap items-end gap-3" data-testid="preset-row">
                <div className="min-w-52 flex-1"><label className="text-xs font-semibold text-slate-500">Autofill from saved package</label>
                  <select data-testid="preset-select" className={`${inputCls} mt-1`} defaultValue="" onChange={(e) => { if (e.target.value) applyPreset(e.target.value); e.target.value = ""; }}>
                    <option value="" disabled>Select a saved preset…</option>
                    {presets.map((p) => <option key={p.preset_id} value={p.preset_id}>{p.name} — {p.weight} kg · {p.length}×{p.breadth}×{p.height} cm</option>)}
                  </select></div>
              </div>
            )}

            {form.shipment_class !== "heavy" && (
              <>
                <div className="mt-4 grid gap-4 sm:grid-cols-4" data-testid="single-weight-dims">
                  <div><label className="text-xs font-semibold text-slate-500">Weight (kg) * {form.package_type === "document" ? <span className="text-amber-600">· max 2 kg</span> : <span className="text-slate-400">· min 0.5 kg</span>}</label>
                    <input data-testid="package-weight-input" className={`${inputCls} mono mt-1`} inputMode="decimal" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value.replace(/[^\d.]/g, "") })} /></div>
                  {[["length", "L (cm) *"], ["breadth", "B (cm) *"], ["height", "H (cm) *"]].map(([k, l]) => (
                    <div key={k}><label className="text-xs font-semibold text-slate-500">{l}</label>
                      <input data-testid={`package-${k}-input`} className={`${inputCls} mono mt-1`} inputMode="decimal" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value.replace(/[^\d.]/g, "") })} /></div>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap items-end gap-3">
                  <div className="min-w-44 flex-1"><label className="text-xs font-semibold text-slate-500">Save these dimensions as a preset</label>
                    <input data-testid="preset-name-input" className={`${inputCls} mt-1`} placeholder="e.g. Small box 0.5kg" maxLength={40} value={presetName} onChange={(e) => setPresetName(e.target.value)} /></div>
                  <button type="button" onClick={savePreset} disabled={presetSaving} data-testid="preset-save-btn"
                    className="flex items-center gap-1.5 rounded-xl border border-shipany-blue/30 px-4 py-2.5 text-xs font-bold text-shipany-blue hover:bg-shipany-blue/5 disabled:opacity-50">
                    <BookmarkPlus className="h-3.5 w-3.5" /> {presetSaving ? "Saving…" : "Save preset"}
                  </button>
                  {presets.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pb-1" data-testid="preset-chips">
                      {presets.slice(0, 4).map((p) => (
                        <span key={p.preset_id} className="flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold text-slate-500">
                          {p.name}
                          <button type="button" onClick={() => deletePreset(p.preset_id)} data-testid={`preset-delete-${p.preset_id}`} className="text-slate-400 hover:text-red-500"><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {form.shipment_class === "heavy" && (
              <div className="mt-4" data-testid="boxes-field">
                <label className="text-xs font-semibold text-slate-500">Number of boxes *</label>
                <input data-testid="package-boxes-input" className={`${inputCls} mono mt-1 max-w-xs`} inputMode="numeric" placeholder="e.g. 2"
                  value={form.boxes || ""} onChange={(e) => {
                    const n = e.target.value.replace(/\D/g, "");
                    setForm((f) => {
                      const count = Math.min(parseInt(n) || 0, 20);
                      const list = Array.from({ length: count }, (_, i) => (f.box_list || [])[i] || { weight: "", length: "10", breadth: "10", height: "10" });
                      return { ...f, boxes: n, box_list: list };
                    });
                  }} />
                <p className="mt-1 text-[11px] text-slate-400">Each box gets its own weight & dimensions — billed weight is calculated per box and summed. Maximum {MAX_BOX_DIMENSION_CM} cm per side.</p>
                {(form.box_list || []).length > 0 && (
                  <div className="mt-3 space-y-3" data-testid="box-list">
                    {form.box_list.map((b, i) => {
                      const bw = parseFloat(b.weight) || 0;
                      const bv = ((parseFloat(b.length) || 0) * (parseFloat(b.breadth) || 0) * (parseFloat(b.height) || 0)) / 5000;
                      const billed = Math.max(bw, bv, 0.1);
                      return (
                        <div key={i} className="rounded-xl border border-slate-200 bg-slate-50/50 p-4" data-testid={`box-card-${i + 1}`}>
                          <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Box {i + 1}</p>
                          <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
                            {[["weight", "Weight (kg)"], ["length", "L (cm)"], ["breadth", "W (cm)"], ["height", "H (cm)"]].map(([k, l]) => (
                              <div key={k}>
                                <label className="text-[10px] font-semibold text-slate-500">{l} *</label>
                                <input data-testid={`box-${i + 1}-${k}-input`} className={`${inputCls} mono mt-1`} inputMode="decimal" placeholder={k === "weight" ? "5" : "10"} max={k === "weight" ? undefined : MAX_BOX_DIMENSION_CM}
                                  value={b[k]} onChange={(e) => {
                                    const v = e.target.value.replace(/[^\d.]/g, "");
                                    setForm((f) => {
                                      const list = f.box_list.map((x, j) => (j === i ? { ...x, [k]: v } : x));
                                      return { ...f, box_list: list, weight: String(list.reduce((s, x) => s + (parseFloat(x.weight) || 0), 0)) };
                                    });
                                  }} />
                              </div>
                            ))}
                          </div>
                          <p className="mono mt-2 text-[11px] text-slate-500" data-testid={`box-billed-${i + 1}`}>
                            actual {bw.toFixed(2)} kg · volumetric {bv.toFixed(2)} kg → billed <span className="font-bold text-emerald-600">{billed.toFixed(2)} kg</span>
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {(() => {
              if (form.shipment_class === "heavy") {
                const rows = (form.box_list || []).map((b) => {
                  const w = parseFloat(b.weight) || 0;
                  const v = ((parseFloat(b.length) || 0) * (parseFloat(b.breadth) || 0) * (parseFloat(b.height) || 0)) / 5000;
                  return Math.max(w, v, 0.1);
                });
                const total = rows.reduce((s, x) => s + x, 0);
                return (
                  <div className="mt-4 rounded-xl bg-emerald-50 p-4 ring-1 ring-emerald-200" data-testid="weight-breakdown">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                      {rows.map((r, i) => (
                        <span key={i} className="mono text-xs text-slate-500" data-testid={`box-billed-summary-${i + 1}`}>Box {i + 1}: <span className="font-bold text-shipany-navy">{r.toFixed(2)} kg</span></span>
                      ))}
                      {!rows.length && <span className="text-xs text-slate-400">Enter box count to add boxes</span>}
                    </div>
                    <p className="mt-2 text-center"><span className="text-[10px] uppercase tracking-widest text-emerald-600">Total chargeable weight</span> <span className="mono ml-2 text-lg font-bold text-emerald-700" data-testid="billed-weight">{total.toFixed(2)} kg</span></p>
                  </div>
                );
              }
              const l = parseFloat(form.length) || 0, b = parseFloat(form.breadth) || 0, h = parseFloat(form.height) || 0;
              const dead = parseFloat(form.weight) || 0;
              const nBoxes = Math.max(parseInt(form.boxes) || 1, 1);
              const vol = (l * b * h) / 5000 * nBoxes;
              const billed = Math.max(dead, vol, 0.5);
              return (
                <div className="mt-4 grid gap-3 sm:grid-cols-3" data-testid="weight-breakdown">
                  <div className="rounded-xl bg-slate-50 p-3 text-center"><p className="text-[10px] uppercase tracking-widest text-slate-400">Actual weight{nBoxes > 1 ? ` (${nBoxes} boxes)` : ""}</p><p className="mono text-lg font-bold text-shipany-navy">{dead.toFixed(2)} kg</p></div>
                  <div className="rounded-xl bg-slate-50 p-3 text-center"><p className="text-[10px] uppercase tracking-widest text-slate-400">Volumetric (L×B×H÷5000{nBoxes > 1 ? ` × ${nBoxes}` : ""})</p><p className="mono text-lg font-bold text-shipany-navy" data-testid="volumetric-weight">{vol.toFixed(2)} kg</p></div>
                  <div className="rounded-xl bg-emerald-50 p-3 text-center ring-1 ring-emerald-200"><p className="text-[10px] uppercase tracking-widest text-emerald-600">Chargeable weight</p><p className="mono text-lg font-bold text-emerald-700" data-testid="billed-weight">{billed.toFixed(2)} kg</p></div>
                </div>
              );
            })()}
          </div>
        )}

        {step === 4 && (
          <div data-testid="wizard-step-other">
            <h2 className="font-display text-lg font-bold text-shipany-navy">Other details</h2>
            <p className="mt-1 text-xs text-slate-400">All optional — for B2B / compliance needs.</p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div><label className="text-xs font-semibold text-slate-500">Reseller name</label>
                <input data-testid="reseller-name-input" className={`${inputCls} mt-1`} placeholder="If you resell for someone" maxLength={60} value={form.reseller_name} onChange={(e) => setForm({ ...form, reseller_name: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-slate-500">Your GSTIN</label>
                <input data-testid="seller-gstin-input" className={`${inputCls} mono mt-1`} placeholder="Optional" maxLength={15} value={form.seller_gstin} onChange={(e) => setForm({ ...form, seller_gstin: e.target.value.toUpperCase() })} /></div>
              <div className="sm:col-span-2"><label className="text-xs font-semibold text-slate-500">GST E-way bill number</label>
                <input data-testid="eway-bill-input" className={`${inputCls} mono mt-1`} placeholder="12 characters — required for invoices above ₹50,000" maxLength={12} value={form.eway_bill} onChange={(e) => setForm({ ...form, eway_bill: e.target.value.replace(/[^A-Za-z0-9]/g, "") })} /></div>
            </div>
          </div>
        )}

        {step === 5 && (
          <div data-testid="wizard-step-rates">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display text-lg font-bold text-shipany-navy">Select operator</h2>
              {!loadingRates && rates && (
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                    <input data-testid="rate-search-input" className="w-44 rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-xs text-shipany-navy placeholder:text-slate-400 focus:border-shipany-blue focus:outline-none" placeholder="Search operator…" value={rateQuery} onChange={(e) => setRateQuery(e.target.value)} />
                  </div>
                  <select data-testid="rate-sort-select" className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-shipany-navy focus:border-shipany-blue focus:outline-none" value={rateSort} onChange={(e) => setRateSort(e.target.value)}>
                    <option value="price">Lowest price first</option>
                    <option value="eta">Fastest first</option>
                  </select>
                </div>
              )}
            </div>
            {loadingRates && (
              <div className="mt-6 flex items-center justify-center gap-3 py-16 text-slate-400" data-testid="rates-loading">
                <Loader2 className="h-5 w-5 animate-spin" /> Fetching live rates from all operators…
              </div>
            )}
            {!loadingRates && rates && (
              <>
                <p className="mono mt-2 text-xs text-slate-400" data-testid="rates-meta">
                  {warehouse?.pincode} → {form.delivery_pincode} · charged {rates.chargeable_weight ?? form.weight} kg · {visibleQuotes.length} operator{visibleQuotes.length === 1 ? "" : "s"}
                </p>
                <div className="mt-3 space-y-2.5" data-testid="rates-list">
                  {visibleQuotes.map((q) => (
                    <button key={q.courier_id} type="button" data-testid={`rate-card-${q.courier_id}`} onClick={() => setSelected(q.courier_id)}
                      className={`flex w-full flex-wrap items-center justify-between gap-3 rounded-xl border-2 p-4 text-left transition-all ${selected === q.courier_id ? "border-shipany-blue bg-shipany-blue/5 glow-blue" : "border-slate-200 hover:border-slate-300"}`}>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-shipany-navy">{q.courier_name}</p>
                          {(q.badges || []).map((b) => (
                            <span key={b} className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${/lowest/i.test(b) ? "bg-amber-100 text-amber-700" : "bg-shipany-cyan/15 text-shipany-blue"}`}>
                              {/lowest/i.test(b) ? <Star className="h-3 w-3" /> : <Zap className="h-3 w-3" />}{b}
                            </span>
                          ))}
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          {q.eta_days} day{q.eta_days === 1 ? "" : "s"} ETA
                          {q.service_type ? ` · ${q.service_type}` : ""}
                          {` · COD ${q.cod_supported !== false ? `available${q.cod_charge ? ` (+${inr(q.cod_charge)})` : ""}` : "not available"}`}
                          {q.rating ? ` · ★${q.rating}` : ""}
                        </p>
                      </div>
                      <p className="mono text-lg font-bold text-shipany-blue">{inr(q.final_price)}</p>
                    </button>
                  ))}
                  {visibleQuotes.length === 0 && (
                    <div className="rounded-xl bg-slate-50 px-4 py-8 text-center text-sm text-slate-400" data-testid="rates-empty">
                      No operator matches{rateQuery ? ` "${rateQuery}"` : ""} on this lane{form.payment_mode === "COD" ? " with COD" : ""}.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {step === 6 && booked && (
          <div className="py-6 text-center" data-testid="booking-success">
            <ConfettiBurst />
            <div style={{ animation: "success-pop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both" }}>
              <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
            </div>
            <h2 className="font-display mt-4 text-2xl font-bold text-shipany-navy" style={{ animation: "success-pop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) 0.08s both" }}>Shipment booked!</h2>
            <p className="mono mt-1 text-2xl font-bold text-shipany-blue" data-testid="booked-awb">{booked.awb}</p>
            <div className="mt-2"><StatusBadge status={booked.status} /></div>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <a href={`${API_BASE}/api/shipments/${booked.shipment_id}/document?type=label`} target="_blank" rel="noreferrer" data-testid="download-label-btn"
                className="flex items-center gap-2 rounded-xl bg-shipany-blue px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3]"><Download className="h-4 w-4" /> {Math.max(parseInt(booked.label_count || booked.boxes || 1), 1) > 1 ? "Download Labels PDF" : "Shipping Label"}</a>
              <a href={`${API_BASE}/api/shipments/${booked.shipment_id}/document?type=invoice`} target="_blank" rel="noreferrer" data-testid="download-invoice-btn"
                className="flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Invoice</a>
              <Link to={`/track/${booked.awb}`} data-testid="track-booked-shipment-btn"
                className="flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"><MapPin className="h-4 w-4" /> Track</Link>
            </div>
          </div>
        )}

        {step < 6 && (
          <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
            <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} data-testid="wizard-back-btn"
              className="flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-40">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            {step >= 1 && (
              <button onClick={saveDraft} disabled={savingDraft} data-testid="draft-save-btn"
                className="flex items-center gap-2 rounded-xl border border-shipany-blue/30 px-5 py-2.5 text-sm font-semibold text-shipany-blue transition-colors hover:bg-shipany-blue/5 disabled:opacity-50">
                {savingDraft ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookmarkPlus className="h-4 w-4" />}
                Save as Draft
              </button>
            )}
            {step < 5 ? (
              <button onClick={next} data-testid="shipment-create-wizard-next-btn"
                className="flex items-center gap-2 rounded-xl bg-shipany-blue px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#0648B3] glow-blue">
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button onClick={book} disabled={!selected || booking} data-testid="wizard-book-btn"
                className="flex items-center gap-2 rounded-xl bg-shipany-cyan px-6 py-2.5 text-sm font-bold text-shipany-navy transition-colors hover:bg-[#0EB4DE] disabled:opacity-50 glow-cyan">
                {booking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Book Shipment {sel ? `· ${inr(sel.final_price)}` : ""}
              </button>
            )}
          </div>
        )}

        <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="packing-guidelines">
          <img src="/packing-instructions.webp" alt="Box packing instructions — 1. Prepare the item, 2. Add inner protection, 3. Use a strong outer box, 4. Seal, label & ship"
            className="w-full" loading="lazy" data-testid="packing-instructions-image" />
        </div>
      </div>
    </div>
  );
}
